import React from "react";
import {
  ArrayInput,
  BooleanInput,
  Create,
  Datagrid,
  DateField,
  Edit,
  Filter,
  FormTab,
  List,
  NumberField,
  NumberInput,
  required,
  SimpleForm,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput
} from "react-admin";

// ============ Plan Tier Resource ============

const PlanTierListFilter = props => (
  <Filter {...props}>
    <TextInput label="Search by name" source="qName" alwaysOn />
    <TextInput label="Search by slug" source="qSlug" />
  </Filter>
);

export function PlanTierList(props) {
  return (
    <List
      {...props}
      sort={{ field: "order", order: "ASC" }}
      filters={<PlanTierListFilter />}
      empty={false}
    >
      <Datagrid rowClick="edit">
        <TextField label="Name" source="name" />
        <TextField label="Slug" source="slug" />
        <NumberField label="Commission %" source="commissionPercent" />
        <NumberField label="Min Fee (pence)" source="minimumFeeCents" />
        <TransactionCapField label="Monthly Cap" source="monthlyTransactionCap" />
        <NumberField label="Order" source="order" />
        <DefaultBadge label="Default" source="isDefault" />
        <ActivePriceField label="Active Price" source="activeStripePriceId" />
        <StripePriceCountField label="All Prices" source="stripePriceIds" />
      </Datagrid>
    </List>
  );
}

function TransactionCapField({ record = {} }) {
  const cap = record.monthlyTransactionCap;
  return <span>{cap === null ? "Unlimited" : cap}</span>;
}

function DefaultBadge({ record = {} }) {
  return record.isDefault ? (
    <span style={{
      background: "#4caf50",
      color: "white",
      padding: "2px 8px",
      borderRadius: "4px",
      fontSize: "12px"
    }}>
      Default
    </span>
  ) : null;
}

function StripePriceCountField({ record = {} }) {
  const priceIds = record.stripePriceIds || [];
  return <span>{priceIds.length} price(s)</span>;
}

function ActivePriceField({ record = {} }) {
  const priceId = record.activeStripePriceId;
  if (!priceId) {
    return (
      <span style={{
        color: "#999",
        fontStyle: "italic",
        fontSize: "12px"
      }}>
        Not set
      </span>
    );
  }
  return (
    <span style={{
      background: "#e3f2fd",
      color: "#1976d2",
      padding: "2px 8px",
      borderRadius: "4px",
      fontSize: "11px",
      fontFamily: "monospace"
    }}>
      {priceId.substring(0, 20)}...
    </span>
  );
}

export function PlanTierCreate(props) {
  return (
    <Create {...props}>
      <SimpleForm>
        <TextInput source="name" label="Name" validate={[required()]} />
        <TextInput
          source="slug"
          label="Slug"
          validate={[required()]}
          helperText="Unique identifier (e.g., 'free', 'lite', 'growth', 'scale')"
        />
        <NumberInput
          source="commissionPercent"
          label="Commission %"
          validate={[required()]}
          helperText="e.g., 3 for 3%"
        />
        <NumberInput
          source="minimumFeeCents"
          label="Minimum Fee (pence)"
          validate={[required()]}
          helperText="e.g., 50 for 50p"
        />
        <NumberInput
          source="monthlyTransactionCap"
          label="Monthly Transaction Cap"
          helperText="Leave empty for unlimited"
        />
        <NumberInput source="order" label="Display Order" defaultValue={0} />
        <BooleanInput
          source="isDefault"
          label="Is Default Tier"
          helperText="Schools without a tier will use this one"
        />
        <TextInput source="description" label="Description" multiline />
        <TextInput
          source="activeStripePriceId"
          label="Active Stripe Price ID"
          helperText="The price used for new subscriptions (e.g., price_1Abc123...)"
          fullWidth
        />
      </SimpleForm>
    </Create>
  );
}

export function PlanTierEdit(props) {
  return (
    <Edit {...props}>
      <TabbedForm>
        <FormTab label="Details">
          <TextInput source="id" label="ID" disabled />
          <TextInput source="name" label="Name" validate={[required()]} />
          <TextInput
            source="slug"
            label="Slug"
            validate={[required()]}
            helperText="Unique identifier"
          />
          <NumberInput
            source="commissionPercent"
            label="Commission %"
            validate={[required()]}
          />
          <NumberInput
            source="minimumFeeCents"
            label="Minimum Fee (pence)"
            validate={[required()]}
          />
          <NumberInput
            source="monthlyTransactionCap"
            label="Monthly Transaction Cap"
            helperText="Leave empty for unlimited"
          />
          <NumberInput source="order" label="Display Order" />
          <BooleanInput source="isDefault" label="Is Default Tier" />
          <TextInput source="description" label="Description" multiline fullWidth />
          <DateField label="Created" source="createdAt" showTime />
          <DateField label="Updated" source="updatedAt" showTime />
        </FormTab>
        <FormTab label="Stripe Prices">
          <div style={{ marginBottom: "24px", padding: "16px", background: "#e3f2fd", borderRadius: "4px", border: "1px solid #90caf9" }}>
            <h4 style={{ margin: "0 0 8px 0", color: "#1565c0" }}>Active Price (for new subscriptions)</h4>
            <TextInput
              source="activeStripePriceId"
              label="Active Stripe Price ID"
              helperText="This is the price that will be used for NEW subscriptions and upgrades"
              fullWidth
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ margin: "0 0 8px 0", color: "#666" }}>All Linked Prices (for matching existing subscriptions)</h4>
            <ArrayInput source="stripePriceIds" label="">
              <SimpleFormIterator>
                <TextInput
                  source=""
                  label="Stripe Price ID"
                  helperText="e.g., price_1Abc123..."
                />
              </SimpleFormIterator>
            </ArrayInput>
          </div>

          <div style={{ padding: "16px", background: "#fff3e0", borderRadius: "4px", border: "1px solid #ffcc80" }}>
            <p style={{ fontSize: "12px", color: "#e65100", margin: 0 }}>
              <strong>Tip:</strong> When you change prices in Stripe, update the "Active Price" to the new one.
              Keep the old price in the list below so existing subscribers can still be matched to this tier.
            </p>
          </div>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}
